import {
  createApp,
  defineComponent,
  h,
  onMounted,
  ref,
  type PropType,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { createPinia } from 'pinia'
import {
  createRouter,
  createWebHistory,
  RouterLink,
  RouterView,
  type RouteRecordRaw,
  useRoute,
} from 'vue-router'
import {
  apiClientKey,
  createApiClient,
} from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import {
  createAppI18n,
  storeLocale,
  type AppLocale,
  type AppLocaleMessages,
} from '@yuksalish/i18n'
import { hasEveryPermission } from '@yuksalish/permissions'
import '@fontsource-variable/inter'
import './styles.css'
export { default as SmartPickerModal } from './SmartPickerModal.vue'
export type { SmartPickerOption } from './SmartPickerModal.vue'

export interface StandaloneAppConfig {
  code: string
  title: string
  description: string
  accent: string
  navigation: Array<string | NavigationItem>
  requiredPermissions?: string[]
  routes?: RouteRecordRaw[]
  messages?: AppLocaleMessages
}

export type LocalizedText = string | Partial<Record<AppLocale, string>>

export interface NavigationItem {
  label: LocalizedText
  to: string
  permission?: string
}

function resolveLocalizedText(
  value: LocalizedText,
  locale: AppLocale,
): string {
  if (typeof value === 'string') {
    return value
  }
  return value[locale] || value.en || value.uz || value.ru || ''
}

export function getRoutePermissions(
  meta: Record<string | number | symbol, unknown>,
): string[] {
  if (Array.isArray(meta.permissions)) {
    return meta.permissions.filter(
      (permission): permission is string => typeof permission === 'string',
    )
  }
  return typeof meta.permission === 'string' ? [meta.permission] : []
}

export function canAccessRoute(
  meta: Record<string | number | symbol, unknown>,
  grantedPermissions: readonly string[],
): boolean {
  return hasEveryPermission(
    grantedPermissions,
    getRoutePermissions(meta),
  )
}

const AppHome = defineComponent({
  name: 'AppHome',
  props: {
    config: {
      type: Object as PropType<StandaloneAppConfig>,
      required: true,
    },
    userName: {
      type: String,
      required: true,
    },
    permissions: {
      type: Array as PropType<string[]>,
      required: true,
    },
    onLogout: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },
  setup(props) {
    const { t, locale } = useI18n()
    const changeLocale = (event: Event) => {
      const nextLocale = (event.target as HTMLSelectElement).value as AppLocale
      locale.value = nextLocale
      storeLocale(nextLocale)
      document.documentElement.lang = nextLocale
    }
    return () =>
      h('div', { class: 'app-frame' }, [
        h('aside', { class: 'app-sidebar' }, [
          h('div', { class: 'app-brand' }, [
            h('span', {
              class: 'app-brand__mark',
            }),
            h('div', [
              h('strong', 'Yuksalish ERP'),
              h('span', props.config.title),
            ]),
          ]),
          h('nav', { 'aria-label': t('common.primaryNavigation') },
            props.config.navigation
              .filter((item) =>
                typeof item === 'string' ||
                !item.permission ||
                props.permissions.includes(item.permission),
              )
              .map((item, index) => {
              if (typeof item === 'string') {
                return h(
                  'button',
                  {
                    class: ['nav-item', { 'nav-item--active': index === 0 }],
                    type: 'button',
                  },
                  item,
                )
              }

              return h(
                RouterLink,
                {
                  class: 'nav-item',
                  to: item.to,
                },
                { default: () => resolveLocalizedText(item.label, locale.value as AppLocale) },
              )
              }),
          ),
          h('div', { class: 'sidebar-user' }, [
            h('div', { class: 'sidebar-user__identity' }, [
              h('span', { class: 'sidebar-user__avatar', 'aria-hidden': 'true' }, props.userName.trim().charAt(0).toUpperCase() || 'U'),
              h('span', { class: 'user-name', title: props.userName }, props.userName),
            ]),
            h('label', { class: 'sidebar-language' }, [
              h('span', t('common.language')),
              h('select', {
                value: locale.value,
                'aria-label': t('common.language'),
                onChange: changeLocale,
              }, [
                h('option', { value: 'uz' }, 'O‘zbekcha'),
                h('option', { value: 'ru' }, 'Русский'),
                h('option', { value: 'en' }, 'English'),
              ]),
            ]),
            h(
              'button',
              {
                class: 'sidebar-signout',
                type: 'button',
                onClick: props.onLogout,
              },
              t('common.signOut'),
            ),
          ]),
        ]),
        h('main', { class: 'app-main' }, [
          props.config.routes?.length
            ? h('section', { class: 'app-content' }, [h(RouterView)])
            : h('section', { class: 'app-content' }, [
                h('h2', 'Workspace ready'),
                h('p', props.config.description),
                h('div', { class: 'module-grid' }, [
                  ...props.config.navigation.map((item) =>
                    h('article', { class: 'module-tile' }, [
                      h('span', {
                        class: 'module-tile__accent',
                      }),
                      h('h3', typeof item === 'string' ? item : resolveLocalizedText(item.label, locale.value as AppLocale)),
                      h('p', 'Feature module boundary'),
                    ]),
                  ),
                ]),
              ]),
        ]),
      ])
  },
})

export function mountStandaloneApp(config: StandaloneAppConfig): void {
  const pinia = createPinia()
  const auth = useAuthStore(pinia)
  const client = createApiClient({
    baseURL: import.meta.env.DEV
      ? (
          String(import.meta.env.VITE_API_DEV_BASE_URL ?? '').trim() ||
          '/api/v1'
        )
      : (
          String(import.meta.env.VITE_API_BASE_URL ?? '').trim() ||
          'https://api.yuksalish.apnet.uz/api/v1'
        ),
    getAccessToken: () => auth.token,
    onUnauthorized: () => auth.clearSession(),
  })

  const ProtectedRoot = defineComponent({
    name: 'ProtectedRoot',
    setup() {
      const { t } = useI18n()
      const route = useRoute()
      const email = ref('')
      const password = ref('')

      onMounted(() => auth.bootstrap(client))

      async function submitLogin(): Promise<void> {
        await auth.login(client, {
          email: email.value.trim(),
          password: password.value,
        })
      }

      return () => {
        if (auth.isBootstrapping) {
          return h('main', { class: 'state-screen' }, [
            h('div', { class: 'state-panel' }, [
              h('span', { class: 'state-loader', 'aria-hidden': 'true' }),
              h('p', t('common.validating')),
            ]),
          ])
        }

        if (!auth.isAuthenticated) {
          return h('main', { class: 'login-screen' }, [
            h('form', {
              class: 'login-panel',
              onSubmit: (event: Event) => {
                event.preventDefault()
                void submitLogin()
              },
            }, [
              h('div', { class: 'login-brand' }, [
                h('span', {
                  class: 'app-brand__mark',
                }),
                h('div', [
                  h('strong', 'Yuksalish ERP'),
                  h('span', config.title),
                ]),
              ]),
              h('h1', t('common.signIn')),
              h('label', { for: 'email' }, t('common.email')),
              h('input', {
                id: 'email',
                type: 'email',
                autocomplete: 'username',
                required: true,
                value: email.value,
                onInput: (event: Event) => {
                  email.value = (event.target as HTMLInputElement).value
                },
              }),
              h('label', { for: 'password' }, t('common.password')),
              h('input', {
                id: 'password',
                type: 'password',
                autocomplete: 'current-password',
                required: true,
                value: password.value,
                onInput: (event: Event) => {
                  password.value = (event.target as HTMLInputElement).value
                },
              }),
              auth.errorMessage
                ? h('p', { class: 'form-error', role: 'alert' }, auth.errorMessage)
                : null,
              h(
                'button',
                {
                  class: 'primary-button',
                  type: 'submit',
                  disabled: auth.isSubmitting,
                },
                auth.isSubmitting ? t('common.signingIn') : t('common.signIn'),
              ),
            ]),
          ])
        }

        const permissions = auth.user?.permissions ?? []
        if (
          !hasEveryPermission(permissions, config.requiredPermissions ?? [])
        ) {
          return h('main', { class: 'state-screen' }, [
            h('div', { class: 'state-panel state-panel--wide' }, [
              h('p', { class: 'app-code' }, config.code),
              h('h1', t('common.accessDenied')),
              h(
                'p',
                t('common.noAppPermission'),
              ),
              h(
                'button',
                {
                  class: 'text-button',
                  type: 'button',
                  onClick: () => void auth.logout(client),
                },
                t('common.signOut'),
              ),
            ]),
          ])
        }

        if (!canAccessRoute(route.meta, permissions)) {
          return h('main', { class: 'state-screen' }, [
            h('div', { class: 'state-panel state-panel--wide' }, [
              h('p', { class: 'app-code' }, config.code),
              h('h1', t('common.accessDenied')),
              h(
                'p',
                t('common.noPagePermission'),
              ),
              h(RouterLink, { to: '/' }, { default: () => t('common.returnOverview') }),
            ]),
          ])
        }

        return h(AppHome, {
          config,
          userName: auth.user?.name ?? t('common.user'),
          permissions,
          onLogout: () => void auth.logout(client),
        })
      }
    },
  })

  const router = createRouter({
    history: createWebHistory(),
    routes: [
      {
        path: '/',
        component: ProtectedRoot,
        children: [
          ...(config.routes ?? []),
          {
            path: 'access-denied',
            name: 'access-denied',
            component: defineComponent({
              setup: () => () =>
                h('div', { class: 'route-state' }, [
                  h('p', { class: 'app-code' }, config.code),
                  h('h2', 'Access denied'),
                  h(
                    'p',
                    'Your account does not have permission to open this page.',
                  ),
                  h(RouterLink, { to: '/' }, { default: () => 'Return to overview' }),
                ]),
            }),
          },
          {
            path: ':pathMatch(.*)*',
            name: 'not-found',
            component: defineComponent({
              setup: () => () =>
                h('div', { class: 'route-state' }, [
                  h('p', { class: 'app-code' }, config.code),
                  h('h2', 'Page not found'),
                  h('p', 'The requested page does not exist in this application.'),
                  h(RouterLink, { to: '/' }, { default: () => 'Return to overview' }),
                ]),
            }),
          },
        ],
      },
    ],
  })

  router.beforeEach((to) => {
    if (
      auth.isAuthenticated &&
      !canAccessRoute(to.meta, auth.user?.permissions ?? [])
    ) {
      return {
        name: 'access-denied',
        query: { from: to.fullPath },
      }
    }

    return true
  })

  const i18n = createAppI18n(config.messages)
  document.documentElement.lang = i18n.global.locale.value

  createApp({ render: () => h(RouterView) })
    .use(pinia)
    .use(i18n)
    .use(router)
    .provide(apiClientKey, client)
    .mount('#app')
}
